import {createInfiniteQuery, type CreateInfiniteQueryOptions, type inferData} from 'react-query-kit'

import {createMemexItemModel} from '../../../models/memex-item-model'
import {buildInitialItemsAndColumns} from '../memex-items-data'
import type {MemexItemsPageQueryData} from './types'

const useMemexItemsQueryKey = 'memexItems'

const infiniteQuerySharedOptions: Pick<
  CreateInfiniteQueryOptions<MemexItemsPageQueryData>,
  'primaryKey' | 'structuralSharing'
> = {
  primaryKey: useMemexItemsQueryKey,
  // from https://tanstack.com/query/v4/docs/react/reference/useQuery
  /**
   *
   * If set to a function, the old and new data values will be passed through this function,
   * which should combine them into resolved data for the query. This way, you can retain references
   * from the old data to improve performance even when that data contains non-serializable values.
   */
  // Ideally, this approach would yield some performance benefits - however, due to the fact that in some cases we're using
  // mutable objects (MemexItemModels) and not changing anything about the contents of the array of items,
  // without overriding the default behavior, we would run into issues where our optimistic updates weren't
  // causing the table to re-render, because react-query did not think that anything had changed about our data.
  structuralSharing: false,
}

export const useMemexItemsQuery = createInfiniteQuery<MemexItemsPageQueryData>({
  ...infiniteQuerySharedOptions,
  // We're not currently using this queryFn, as the query is disabled
  // and we're relying on optimistic updates to mutate the state
  // However, this is a required prop to `createQuery`,
  // and helps determine the type of data stored in the query cache
  queryFn: () => {
    return {nodes: [], pageInfo: {startCursor: '', endCursor: '', hasNextPage: false, hasPreviousPage: false}}
  },
  enabled: false,
  // Read our initial data from the JSON island
  initialData: () => {
    const {memexItems} = buildInitialItemsAndColumns('memex-items-data')
    const initialItems = memexItems || []
    const firstPage = {
      nodes: initialItems.map(item => createMemexItemModel(item)),
      pageInfo: {
        startCursor: '',
        endCursor: '',
        hasNextPage: false,
        hasPreviousPage: false,
      },
    }
    return {pages: [firstPage], pageParams: []}
  },
})

export type UseMemexItemsQueryData = inferData<typeof useMemexItemsQuery>
